import React, { useState } from 'react';
import NavigationBar from '../navbar/NavigationBar';
import Footer from '../navbar/Footer';
import DownloadIcon from '@mui/icons-material/Download';
import {
  Container,
  Typography,
  Grid,
  Card,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  styled,
  createTheme,
  ThemeProvider,
  Box,
  CardMedia,
  Button,
} from '@mui/material';
import moment from 'moment';

const statistiquesSolidaireData = [
    {
    ministere: 'Ministère de la Solidarité Nationale, des Droits de l’Homme et du Genre',
    annuaires: 'Annuaire statistique Édition 2023',
    description: 'La compilation des données statistiques de 2022 et de 2023 essentielles sur les programmes et initiatives mis en œuvre par le Ministère afin de promouvoir les droits humains et renforcer la solidarité nationale. Il évalue les progrès réalisés tout en identifiant les défis persistants.',
    date: "2024-12-31",
    link: "/documents/Sectorials/ANNUAIRE-STATISTIQUE-EDITION-2023-DU-MINISTERE-DE-LA-SOLIDARITE-NATIONALE-VERSION-FINALE-PUBLIEE.pdf"
  },
  {
    ministere: 'Ministère de la Solidarité Nationale, des Droits de l’Homme et du Genre',
    annuaires: 'Annuaire statistique Édition 2021',
    description: 'La compilation des données statistiques essentielles sur les programmes et initiatives mis en œuvre par le Ministère afin de promouvoir les droits humains et renforcer la solidarité nationale. Il évalue les progrès réalisés tout en identifiant les défis persistants.',
    date: "2021-12-31",
    link: "/documents/Sectorials/ANNUAIRE-STATISTIQUE-2021-DU-MINISTERE-DE-LA-SOLIDARITE-NATIONALE-VERSION-PUBLIEE.pdf"
  },
  {
    ministere: 'Ministère de la Solidarité Nationale, des Droits de l’Homme et du Genre',
    annuaires: 'Annuaire statistique Édition 2020',
    description: "L'analyse des résultats et les impacts des politiques de solidarité et de protection des droits humains pour l’année 2020, fournissant un aperçu des initiatives clés et de leurs effets sur la population.",
    date: "2020-12-31",
    link: "/documents/Sectorials/Annuaire-statistique-2020-3.pdf"
  },
  {
    ministere: 'Ministère de la Solidarité Nationale, des Droits de l’Homme et du Genre',
    annuaires: 'Annuaire statistique Édition 2019',
    description: 'Une évaluation approfondie des politiques de solidarité et des droits humains, comprenant des données sur l’accès aux services essentiels et les résultats des programmes de soutien durant l\'année 2019.',
    date: "2019-12-31",
    link: "/documents/Sectorials/Annuaire-statistique-2019-2.pdf"
  },
  {
    ministere: 'Ministère de la Solidarité Nationale, des Droits de l’Homme et du Genre',
    annuaires: 'Annuaire statistique Édition 2018',
    description: 'Un état des lieux des initiatives de solidarité et des mesures prises pour le respect des droits humains, offrant des statistiques sur les populations vulnérables et les actions entreprises pour leur aide.',
    date: "2018-12-31",
    link: "/documents/Sectorials/Annuaire-statistique-2018-1.pdf"
  },
];

function MinistereSolidaire() {
  const [visibleCount, setVisibleCount] = useState(4);
  const [selectedYear, setSelectedYear] = useState('Tous');
  const [filteredStatistiques, setFilteredStatistiques] = useState(statistiquesSolidaireData);

  const handleShowMore = () => {
    setVisibleCount((prevCount) => prevCount + 2);
  };

  const handleYearChange = (event) => {
    const year = event.target.value;
    setSelectedYear(year);
    if (year === 'Tous') {
      setFilteredStatistiques(statistiquesSolidaireData);
    } else {
      const filtered = statistiquesSolidaireData.filter((statistique) => {
        const statistiqueYear = new Date(statistique.date).getFullYear();
        return statistiqueYear === parseInt(year);
      });
      setFilteredStatistiques(filtered);
    }
  };

  const StyledSelect = styled(Select)(({ theme }) => ({
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.primary.main,
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.primary.light,
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.primary.dark,
    },
  }));

  const theme = createTheme({
    palette: {
      primary: {
        main: '#388E3C',
      },
      background: {
        default: '#f5f5f5',
      },
    },
    typography: {
      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
      body1: {
        fontSize: 16,
        letterSpacing: 0.5,
        color: '#6B778C',
      },
      h4: {
        fontWeight: '600',
      },
    },
  });

  return (
    <ThemeProvider theme={theme}>
      <div style={{ backgroundColor: theme.palette.background.default, paddingBottom: '32px' }}>
        <NavigationBar />
        <Container maxWidth="lg" style={{ paddingTop: '54px' }}>
          <Typography variant="h4" align="justify" gutterBottom style={{ color: theme.palette.primary.main, marginBottom: '16px' }}>
            Ministère de la Solidarité Nationale, des Droits de l’Homme et du Genre
          </Typography>
          <Divider style={{ margin: '16px 0' }} />
          <Typography variant="body1" align="justify" paragraph style={{ marginBottom: '24px' }}>
            Le Ministère de la Solidarité Nationale, des Droits de l'Homme et du Genre s'engage à promouvoir la justice sociale et à garantir l'égalité des droits pour tous. Il joue un rôle crucial dans le renforcement de la solidarité nationale et la protection des droits humains. Cette page présente des statistiques importantes sur les initiatives et programmes du ministère.
          </Typography>

          <Typography variant="body1" align="left" style={{ fontWeight: '500', marginBottom: '8px' }}>
            Filtrer par année de réalisation :
          </Typography>
          <FormControl fullWidth variant="outlined">
            <InputLabel id="year-select-label">Année</InputLabel>
            <StyledSelect
              labelId="year-select-label"
              id="year-select"
              value={selectedYear}
              onChange={handleYearChange}
              label="Année"
            >
              <MenuItem value="Tous">Tous les annuaires</MenuItem>
              {[...new Set(statistiquesSolidaireData.map((statistique) => new Date(statistique.date).getFullYear()))]
                .sort()
                .map((year) => (
                  <MenuItem key={year} value={year.toString()}>
                    {year}
                  </MenuItem>
                ))}
            </StyledSelect>
          </FormControl>

          {/* Affichage des Statistiques */}
          <Grid container spacing={1} style={{ marginTop: '5px' }}>
            {filteredStatistiques.slice(0, visibleCount).map((statistique, index) => (
              <Grid item xs={12} sm={12} key={index}>
                <Card elevation={3} style={{ borderRadius: '12px', overflow: 'hidden' }}>
                  <Box sx={{ display: 'flex' }}>
                    <a
                      href={statistique.link}
                      style={{ textDecoration: 'none', color: 'inherit' }}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <CardMedia
                        component="img"
                        sx={{ width: 71, height: 100, m: 2 }}
                        image={"assets/annuaires/mini-solidalite.png"} 
                        alt={statistique.annuaires}
                      />
                    </a>
                    <Box sx={{ display: 'flex', flexDirection: 'column', pl: 2, pt: 2 }}>
                      <Typography variant="h6" component="div" style={{ fontWeight: 'bold', marginBottom: '10px', color: theme.palette.primary.main }}>
                        <a
                          href={statistique.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ textDecoration: 'none', color: 'inherit' }}
                        >
                         {<DownloadIcon />} {statistique.annuaires}
                        </a>
                      </Typography>
                      <Typography variant="body2" component="div" style={{ color: '#555', marginBottom: '10px' }}>
                        {statistique.description}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        Publié en {moment(statistique.date).format('MMMM YYYY')}
                      </Typography>
                    </Box>
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>

          {visibleCount < filteredStatistiques.length && (
            <Button
              onClick={handleShowMore}
              style={{
                marginTop: '20px',
                backgroundColor: theme.palette.primary.main,
                color: 'white',
              }}
            >
              Plus de Statistiques
            </Button>
          )}
        </Container>
        <br />
        <br />
        <Footer />
      </div>
    </ThemeProvider>
  );
}

export default MinistereSolidaire;